/**
 * Runtime configuration — mirror of the Swift app's `BrainConfig` + the
 * `AppEnvironment.Backend` go-live switch.
 *
 * Every value has a safe PUBLIC default (the Brain Worker URL, the Supabase
 * project URL, and the Supabase **publishable/anon** key — designed to ship in
 * clients; RLS still gates rows) and is overridable via Vite env vars at build
 * time. NO secret or password is ever stored here; the only durable credential
 * is the user's `brain_` token (obtained by logging in) which lives in
 * localStorage, never in source.
 */

export type Backend = 'mock' | 'supabase';

export interface BrainConfig {
  /** Brain Worker base URL (hosts /login + /supabase-token). */
  brainBaseURL: string;
  /** Supabase project URL. */
  supabaseURL: string;
  /** Supabase publishable/anon key (safe for clients; RLS still gates rows). */
  supabaseAnonKey: string;
}

// Vite exposes only `VITE_`-prefixed vars to client code. import.meta.env is
// statically replaced at build time.
const env = import.meta.env;

/**
 * The go-live switch (the ONE toggle). DEFAULT is Mock so the app builds and
 * runs fully offline. Set `VITE_FACTORY_BACKEND=supabase` to select the real
 * Supabase + BrainAuth path.
 */
export const selectedBackend: Backend =
  (env.VITE_FACTORY_BACKEND ?? '').toLowerCase() === 'supabase' ? 'supabase' : 'mock';

/**
 * Known-good defaults for the `claude-brain` project + Worker, with per-field
 * Vite env overrides. Publishable/anon key only — no secret.
 */
export function brainConfigFromEnvironment(): BrainConfig {
  return {
    brainBaseURL:
      env.VITE_FACTORY_BRAIN_URL ?? 'https://claude-brain.suleimankhan11.workers.dev',
    supabaseURL:
      env.VITE_FACTORY_SUPABASE_URL ?? 'https://qmttigbqvpgzpfmdmktm.supabase.co',
    supabaseAnonKey:
      env.VITE_FACTORY_SUPABASE_ANON_KEY ?? 'sb_publishable_IYRiCM7XDH9jynhalEPyLg_7S_5rA-Q',
  };
}

/**
 * Optional credentials for a headless/auto sign-in on the Supabase path. As with
 * the Swift app these are read at runtime and never baked in; if absent, the app
 * expects `BrainAuth.login(email, password)` to be driven from a sign-in UI.
 */
export function brainCredentialsFromEnvironment(): { email?: string; password?: string } {
  return {
    email: env.VITE_FACTORY_BRAIN_EMAIL,
    password: env.VITE_FACTORY_BRAIN_PASSWORD,
  };
}
